import { useEffect, useLayoutEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { ChevronUp, Check } from 'lucide-react'
import { useVoiceStore } from '../../stores/voiceStore'
import { useLivekitStore } from '../../stores/livekitStore'
import { requestVideoStream } from '../../services/cameraPermission'
import AutoFaceCenterButton from './AutoFaceCenterButton'
import CameraZoomPicker from './CameraZoomPicker'
import BackgroundEffectPicker from './BackgroundEffectPicker'
import CameraEnhancePicker from './CameraEnhancePicker'

// OSPROD-101: camera (videoinput) picker, mirroring AudioDeviceSelector.
// The little ChevronUp overlay sits on the camera button; the dropdown
// lists the available cameras and switches the live publication via
// livekitStore.setVideoDevice → mediaManager.setVideoDevice. The choice
// is persisted in voiceStore (localStorage) so the checkmark survives a
// reload. Per-device video previews / mirror-toggle / pre-join testing
// are intentionally NOT here — that's OSPROD-103.
//
// OSPROD-134: this dropdown now also hosts the three camera-settings
// pickers that used to sit as standalone icons on the main toolbar
// (Auto-frame, Camera zoom, Background effect). They render as a mini
// toolbar row inside the menu; each portals its own popup so nested
// positioning works without layout fuss. The toolbar is now Mic / Cam
// / Screen share / Speaker / + utilities — every camera-only setting
// lives behind the Cam chevron instead of cluttering the row.

interface VideoDeviceInfo {
  deviceId: string
  label: string
}

export default function VideoDeviceSelector() {
  const { videoInputDeviceId, setVideoInputDevice } = useVoiceStore()
  const [devices, setDevices] = useState<VideoDeviceInfo[]>([])
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  // OSPROD-128: portal-ize the popup (same pattern as AudioDeviceSelector
  // since OSPROD-110) so it escapes overflow-hidden ancestors and floats
  // above the left sidebar / playback popover.
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({})
  useLayoutEffect(() => {
    if (!open) return
    const compute = () => {
      const trigger = triggerRef.current
      if (!trigger) return
      const r = trigger.getBoundingClientRect()
      const margin = 8
      const menuWidth = 240
      let right = window.innerWidth - r.right
      if (window.innerWidth - right - menuWidth < margin) {
        right = window.innerWidth - menuWidth - margin
      }
      if (right < margin) right = margin
      const bottom = window.innerHeight - r.top + margin
      setMenuStyle({ position: 'fixed', right, bottom, zIndex: 9999 })
    }
    compute()
    window.addEventListener('resize', compute)
    window.addEventListener('scroll', compute, true)
    return () => {
      window.removeEventListener('resize', compute)
      window.removeEventListener('scroll', compute, true)
    }
  }, [open])

  const loadDevices = async () => {
    try {
      // Request permission first so labels are populated, then STOP the
      // resulting track immediately — exactly like AudioDeviceSelector.
      // Leaving the permission capture live would hold the camera open
      // (LED on) every time the dropdown mounts or a `devicechange`
      // fires. One-shot grant must never outlive enumerateDevices.
      let permissionStream: MediaStream | null = null
      try {
        // OSPROD-145: route through cameraPermission circuit breaker so a
        // Chrome "site permission = Block" doesn't fire every devicechange
        // tick into another denied gUM. requestVideoStream throws a cached
        // NotAllowedError once the breaker is engaged.
        permissionStream = await requestVideoStream({ video: true })
      } catch {
        // Permission denied → labels stay anonymous but the picker still
        // lists deviceIds.
      }
      const all = await navigator.mediaDevices.enumerateDevices()
      if (permissionStream) {
        permissionStream.getTracks().forEach((t) => {
          try { t.stop() } catch { /* noop */ }
        })
      }
      const videoInputs = all
        .filter((d) => d.kind === 'videoinput')
        .map((d) => ({ deviceId: d.deviceId, label: d.label || `Camera ${d.deviceId.slice(0, 4)}` }))
      setDevices(videoInputs)

      // Auto-select the first camera if nothing is chosen yet.
      if (!videoInputDeviceId && videoInputs.length > 0) {
        setVideoInputDevice(videoInputs[0].deviceId)
      }
    } catch {
      // enumerateDevices unavailable / blocked
    }
  }

  useEffect(() => {
    loadDevices()
    navigator.mediaDevices.addEventListener('devicechange', loadDevices)
    return () => navigator.mediaDevices.removeEventListener('devicechange', loadDevices)
  }, [])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as Node
      if (
        containerRef.current && !containerRef.current.contains(t) &&
        (!menuRef.current || !menuRef.current.contains(t))
      ) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [open])

  const handleSelect = async (deviceId: string) => {
    setVideoInputDevice(deviceId)
    setOpen(false)
    // Switch the live camera on the active voice room. When the camera is
    // off this is a no-op enable; the choice is still persisted above and
    // applies the next time the user turns the camera on.
    if (useVoiceStore.getState().currentChannelId) {
      try {
        await useLivekitStore.getState().setVideoDevice(deviceId)
      } catch (err) {
        console.error('Failed to switch camera device:', err)
      }
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v) }}
        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-background ring-1 ring-border shadow-sm flex items-center justify-center hover:bg-accent transition z-10"
        title="Select camera"
      >
        <ChevronUp size={12} className="text-foreground" />
      </button>

      {open && createPortal(
        <div ref={menuRef} style={menuStyle} className="bg-card rounded-lg shadow-lg ring-1 ring-border py-1 w-60">
          <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground uppercase">Camera</div>
          {devices.length === 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground">No cameras available</div>
          )}
          {devices.map((d) => (
            <button
              key={d.deviceId}
              onClick={() => handleSelect(d.deviceId)}
              className="w-full text-left px-3 py-1.5 text-xs text-muted-foreground hover:bg-primary/90 hover:text-white transition flex items-center gap-2"
            >
              {videoInputDeviceId === d.deviceId && <Check size={12} className="text-green-500 shrink-0" />}
              <span className="flex-1 truncate">{d.label}</span>
            </button>
          ))}
          {/* OSPROD-134: settings row — same affordances that used to
              sit as separate icons on the call toolbar. Each picker
              renders its own popover via portal, so clicking inside the
              dropdown opens the sub-popup ABOVE this menu without any
              z-index drama. The outside-click handler scoped above
              (containerRef + menuRef) keeps the camera dropdown open
              while a sub-popup is interacted with — its trigger button
              lives inside menuRef. */}
          <div className="my-1 border-t border-border" />
          <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground uppercase">Settings</div>
          <div className="px-3 py-2 flex items-center justify-around">
            <AutoFaceCenterButton size="sm" />
            <CameraZoomPicker compact />
            <BackgroundEffectPicker compact />
            <CameraEnhancePicker compact />
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}
